import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import "./index.css";
import { router } from "./router/routes";
import { LoadingOverlay } from "./components/LoadingOverlay";

declare global {
  interface Window {
    __MSW_ENABLED__?: boolean;
    __MSW_SESSION_ID__?: string;
  }
}

// Initialize Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/backend\.swiftmenu\.com\.br/,
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

async function enableBrowserMocks() {
  const shouldEnableMocks =
    import.meta.env.VITE_MSW_ENABLED === "true" || window.__MSW_ENABLED__;

  if (!shouldEnableMocks) return;

  const { worker } = await import("./mocks/browser");
  await worker.start({ onUnhandledRequest: "bypass" });

  const sessionId = window.__MSW_SESSION_ID__;
  const resetKey = sessionId ? `__MSW_AUTH_RESET__:${sessionId}` : null;
  if (!resetKey || !sessionStorage.getItem(resetKey)) {
    const resetUrl = sessionId
      ? `/__msw/reset-auth?__mswSession=${encodeURIComponent(sessionId)}`
      : "/__msw/reset-auth";
    await fetch(resetUrl);
    if (resetKey) {
      sessionStorage.setItem(resetKey, "true");
    }
  }
}

function renderApp() {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={
          <div className="flex min-h-screen items-center justify-center p-4">
            <p>Something went wrong. Please refresh the page.</p>
          </div>
        }
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <LoadingOverlay />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
}

enableBrowserMocks().finally(renderApp);
